import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Toolbar,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  TableChart as ExcelIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Fullscreen as FullscreenIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import type { UploadedFile } from '@taxassist/api-client';
import { formatFileSize, getFileExtension, canPreviewFile } from '../../utils/fileUtils';
import { formatJobDate } from '../../utils/jobUtils';

interface FileViewerDialogProps {
  open: boolean;
  files: UploadedFile[];
  initialFileIndex?: number;
  onClose: () => void;
  onDownload?: (file: UploadedFile) => void;
  title?: string;
  getFileUrl?: (file: UploadedFile) => Promise<string>;
}

const FILE_TYPE_ICONS: Record<string, React.ReactElement> = {
  'application/pdf': <PdfIcon />,
  'image/jpeg': <ImageIcon />,
  'image/png': <ImageIcon />,
  'image/gif': <ImageIcon />,
  'application/vnd.ms-excel': <ExcelIcon />,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <ExcelIcon />,
  'application/msword': <DocIcon />,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': <DocIcon />,
};

const getFileIcon = (contentType?: string): React.ReactElement => {
  if (!contentType) return <FileIcon />;
  return FILE_TYPE_ICONS[contentType] || <FileIcon />;
};

export function FileViewerDialog({
  open,
  files,
  initialFileIndex = 0,
  onClose,
  onDownload,
  title = 'File Viewer',
  getFileUrl
}: FileViewerDialogProps) {
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [zoom, setZoom] = useState(1);

  // Reset to initial file when dialog opens or files change
  useEffect(() => {
    const validIndex = Math.max(0, Math.min(initialFileIndex, files.length - 1));
    setCurrentFileIndex(validIndex);
  }, [initialFileIndex, files.length, open]);

  const handleFileSelect = useCallback((index: number) => {
    setCurrentFileIndex(index);
    setZoom(1); // Reset zoom when switching files
  }, []);

  const handleDownload = useCallback(() => {
    if (onDownload && files.length > 0) {
      const currentFile = files[currentFileIndex];
      if (currentFile) {
        onDownload(currentFile);
      }
    }
  }, [onDownload, files, currentFileIndex]);

  const navigateToNext = useCallback(() => {
    const nextIndex = Math.min(currentFileIndex + 1, files.length - 1);
    if (nextIndex !== currentFileIndex) {
      handleFileSelect(nextIndex);
    }
  }, [currentFileIndex, files.length, handleFileSelect]);

  const navigateToPrevious = useCallback(() => {
    const prevIndex = Math.max(currentFileIndex - 1, 0);
    if (prevIndex !== currentFileIndex) {
      handleFileSelect(prevIndex);
    }
  }, [currentFileIndex, handleFileSelect]);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  }, []);


  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keyboard events when dialog is open
      if (!open) return;

      // Helper to check if current file can be previewed (for zoom operations)
      const canCurrentFileBeZoomed = () => {
        if (files.length === 0) return false;
        const currentFile = files[currentFileIndex];
        return canPreviewFile(currentFile?.contentType, currentFile?.fileName);
      };

      // Helper to handle zoom operations
      const handleZoomOperation = (operation: () => void) => {
        if (canCurrentFileBeZoomed()) {
          event.preventDefault();
          operation();
        }
      };

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          navigateToNext();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          navigateToPrevious();
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        case '=':
        case '+':
          handleZoomOperation(handleZoomIn);
          break;
        case '-':
        case '_':
          handleZoomOperation(handleZoomOut);
          break;
        case '0':
          handleZoomOperation(() => setZoom(1));
          break;
        case 'd':
        case 'D':
          if (onDownload && files.length > 0) {
            event.preventDefault();
            handleDownload();
          }
          break;
        default:
          // Allow other keys to bubble up
          break;
      }
    };

    // Add event listener when dialog is open
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, navigateToNext, navigateToPrevious, onClose, files, currentFileIndex, handleZoomIn, handleZoomOut, onDownload, handleDownload]);

  // Handle empty files array
  if (!files || files.length === 0) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="file-viewer-dialog-title"
      >
        <DialogTitle id="file-viewer-dialog-title">
          {title}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No files to display
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const currentFile = files[currentFileIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: { height: '80vh' }
        }
      }}
      aria-labelledby="file-viewer-dialog-title"
    >
      <DialogTitle id="file-viewer-dialog-title">
        {currentFile?.fileName || title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        {/* File List Sidebar */}
        <Box
          sx={{
            width: 300,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Files ({files.length})
            </Typography>
          </Box>
          
          <List sx={{ flex: 1, overflow: 'auto', pt: 0 }}>
            {files.map((file, index) => (
              <ListItem key={file.s3Key} disablePadding>
                <ListItemButton
                  selected={index === currentFileIndex}
                  onClick={() => handleFileSelect(index)}
                  data-testid="file-list-item"
                  aria-selected={index === currentFileIndex}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.50',
                      borderRight: 3,
                      borderRightColor: 'primary.main',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getFileIcon(file.contentType)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: index === currentFileIndex ? 600 : 400,
                        }}
                        title={file.fileName}
                      >
                        {file.fileName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.fileSize)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* File Display Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Simplified File Header - Only for non-previewable files */}
          {!canPreviewFile(currentFile.contentType, currentFile.fileName) && (
            <Box sx={{ p: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" sx={{ mr: 2, wordBreak: 'break-word' }}>
                  {currentFile.fileName}
                </Typography>
                {onDownload && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    aria-label="download current file"
                  >
                    Download
                  </Button>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  label={currentFile.contentType || 'Unknown'}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={formatFileSize(currentFile.fileSize)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={formatJobDate(currentFile.uploadedAt)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          )}

          {/* Compact Navigation and Zoom Controls */}
          <Toolbar variant="dense" sx={{ minHeight: 40, px: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
              {/* Navigation Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Previous file (←)">
                  <span>
                    <IconButton 
                      size="small" 
                      onClick={navigateToPrevious}
                      disabled={currentFileIndex === 0}
                    >
                      <PrevIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                
                <Typography variant="body2" color="text.secondary" sx={{ mx: 1, fontSize: '0.875rem' }}>
                  {currentFileIndex + 1} of {files.length}
                </Typography>
                
                <Tooltip title="Next file (→)">
                  <span>
                    <IconButton 
                      size="small" 
                      onClick={navigateToNext}
                      disabled={currentFileIndex === files.length - 1}
                    >
                      <NextIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {/* Zoom Controls - Only for previewable files */}
              {canPreviewFile(currentFile.contentType, currentFile.fileName) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="Zoom out (-)">
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={handleZoomOut}
                        disabled={zoom <= 0.25}
                      >
                        <ZoomOutIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  
                  <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center', fontSize: '0.75rem' }}>
                    {Math.round(zoom * 100)}%
                  </Typography>
                  
                  <Tooltip title="Zoom in (+)">
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={handleZoomIn}
                        disabled={zoom >= 3}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  
                  <Tooltip title="Reset zoom (0)">
                    <IconButton 
                      size="small" 
                      onClick={() => setZoom(1)}
                      disabled={zoom === 1}
                    >
                      <FullscreenIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Toolbar>

          {/* File Content Area */}
          <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: 'grey.50' }}>
            <FileContentDisplay 
              file={currentFile} 
              zoom={zoom}
              getFileUrl={getFileUrl}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ py: 1 }}>
        <Box sx={{ mr: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            ←/→ navigate • ESC close{canPreviewFile(currentFile?.contentType, currentFile?.fileName) && ' • +/- zoom'}
          </Typography>
        </Box>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Enhanced component for file content display
function FileContentDisplay({ 
  file, 
  zoom = 1, 
  getFileUrl 
}: { 
  file: UploadedFile;
  zoom?: number;
  getFileUrl?: (file: UploadedFile) => Promise<string>;
}) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to trigger file download
  const triggerFileDownload = useCallback((url: string, fileName: string) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const canPreview = canPreviewFile(file.contentType, file.fileName);
  
  // Load file URL when file changes or component mounts
  useEffect(() => {
    if (canPreview && getFileUrl) {
      setLoading(true);
      setError(null);
      setFileUrl(null);
      
      getFileUrl(file)
        .then(url => {
          setFileUrl(url);
          setError(null);
        })
        .catch(err => {
          console.error('Failed to get file URL:', err);
          setError('Failed to load file preview');
          setFileUrl(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [file, canPreview, getFileUrl]);

  if (loading) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 4
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading file preview...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }
  
  if (!canPreview) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 4
      }}>
        <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
          <CardContent>
            {getFileIcon(file.contentType)}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Cannot preview this file type
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              File type: {file.contentType || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Extension: .{getFileExtension(file.fileName)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Use the download button to view this file
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Determine actual content type (handle application/octet-stream)
  const effectiveContentType = (() => {
    if (file.contentType && file.contentType !== 'application/octet-stream') {
      return file.contentType;
    }
    // Try to determine from file extension
    const ext = getFileExtension(file.fileName).toLowerCase();
    const mimeTypes: Record<string, string> = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'pdf': 'application/pdf',
    };
    return mimeTypes[ext] || file.contentType;
  })();

  // Render image files
  if (effectiveContentType?.startsWith('image/')) {
    return (
      <Box sx={{ 
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        overflow: 'auto',
        position: 'relative'
      }}>
        {fileUrl ? (
          <>
            <img
              src={fileUrl}
              alt={file.fileName}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `scale(${zoom})`,
                transformOrigin: 'center',
                transition: 'transform 0.2s ease-in-out',
                objectFit: 'contain',
                cursor: zoom > 1 ? 'grab' : 'default'
              }}
              onError={() => setError('Failed to load image')}
            />
            {/* Overlay Download Button */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1000,
                opacity: 0.8,
                '&:hover': {
                  opacity: 1,
                },
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              <IconButton
                onClick={() => fileUrl && triggerFileDownload(fileUrl, file.fileName)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                  boxShadow: 2,
                }}
                aria-label="download image"
                title="Download image"
              >
                <DownloadIcon />
              </IconButton>
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No preview URL available
          </Typography>
        )}
      </Box>
    );
  }

  // Render PDF files
  if (effectiveContentType === 'application/pdf') {
    // Add URL parameters to hide PDF viewer controls
    const pdfUrl = fileUrl ? `${fileUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0&view=FitH` : null;
    
    return (
      <Box sx={{ height: '100%', overflow: 'auto', position: 'relative' }}>
        {pdfUrl ? (
          <>
            <iframe
              src={pdfUrl}
              title={file.fileName}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                transform: `scale(${zoom})`,
                transformOrigin: 'top left'
              }}
              onError={() => setError('Failed to load PDF')}
            />
            {/* Overlay Download Button */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1000,
                opacity: 0.8,
                '&:hover': {
                  opacity: 1,
                },
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              <IconButton
                onClick={() => fileUrl && triggerFileDownload(fileUrl, file.fileName)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                  boxShadow: 2,
                }}
                aria-label="download PDF"
                title="Download PDF"
              >
                <DownloadIcon />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            p: 4
          }}>
            <Typography variant="body2" color="text.secondary">
              No preview URL available
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Fallback for supported but unhandled file types
  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      p: 4
    }}>
      <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
        <CardContent>
          {getFileIcon(file.contentType)}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Preview not implemented
          </Typography>
          <Typography variant="body2" color="text.secondary">
            File type: {file.contentType || 'Unknown'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Preview support for this file type is coming soon
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}